import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, FolderTree, Edit3, Trash2, Star, StarOff, ChevronRight, ChevronDown,
  Loader2, X, ImageIcon, Eye, EyeOff,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { adminApi } from '@/services/api';
import { Badge, Button, Card, CardContent, Input, useConfirm } from '@nepal-ecom/ui';

interface Category {
  id: string;
  name: string;
  nameNp: string | null;
  slug: string;
  description: string | null;
  imageUrl: string | null;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  isFeatured: boolean;
  children?: Category[];
}

export function CategoryList() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Category | null>(null);
  const [creating, setCreating] = useState<{ parentId: string | null } | null>(null);

  const { data: tree, isLoading } = useQuery({
    queryKey: ['admin', 'categories', 'tree'],
    queryFn: async () => {
      const res = await adminApi.getCategoryTree();
      return res.data.data as Category[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Category> }) =>
      adminApi.updateCategory(id, data),
    onSuccess: () => {
      toast.success('Category updated');
      qc.invalidateQueries({ queryKey: ['admin', 'categories'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteCategory(id),
    onSuccess: () => {
      toast.success('Category removed');
      qc.invalidateQueries({ queryKey: ['admin', 'categories'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Delete failed'),
  });

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const flatCount = useMemo(() => {
    const count = (nodes: Category[]): number =>
      nodes.reduce((sum, n) => sum + 1 + (n.children ? count(n.children) : 0), 0);
    return tree ? count(tree) : 0;
  }, [tree]);

  const renderNode = (node: Category, depth: number = 0) => {
    const hasChildren = (node.children?.length ?? 0) > 0;
    const isExpanded = expanded.has(node.id);
    return (
      <div key={node.id}>
        <div
          className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 border-b border-slate-100 group"
          style={{ paddingLeft: `${16 + depth * 24}px` }}
        >
          <button
            onClick={() => hasChildren && toggleExpand(node.id)}
            className="text-slate-400 hover:text-slate-700 w-4 flex-shrink-0"
          >
            {hasChildren ? (isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="inline-block w-4" />}
          </button>

          {node.imageUrl ? (
            <img src={node.imageUrl} alt="" className="h-7 w-7 rounded object-cover bg-slate-100 flex-shrink-0" />
          ) : (
            <div className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
              <ImageIcon className="h-3 w-3 text-slate-400" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">
              {node.name}
              {node.nameNp && <span className="ml-2 text-xs text-slate-400">{node.nameNp}</span>}
            </p>
            <p className="text-xs text-slate-400 font-mono">/{node.slug}</p>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {node.isFeatured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
            {!node.isActive && <Badge variant="secondary" className="text-[10px]">Hidden</Badge>}
            <span className="text-xs text-slate-400 tabular-nums w-8 text-right">{node.sortOrder}</span>
          </div>

          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button size="sm" variant="ghost" onClick={() => setCreating({ parentId: node.id })} title="Add subcategory">
              <Plus className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => updateMutation.mutate({ id: node.id, data: { isFeatured: !node.isFeatured } })}
              title={node.isFeatured ? 'Unfeature' : 'Feature'}
            >
              {node.isFeatured ? <StarOff className="h-3.5 w-3.5" /> : <Star className="h-3.5 w-3.5" />}
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => updateMutation.mutate({ id: node.id, data: { isActive: !node.isActive } })}
              title={node.isActive ? 'Hide' : 'Show'}
            >
              {node.isActive ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(node)} title="Edit">
              <Edit3 className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm" variant="ghost"
              className="text-red-600 hover:bg-red-50"
              onClick={async () => {
                if (await confirm({
                  title: `Delete "${node.name}"?`,
                  description: 'Subcategories will be unparented.',
                  confirmLabel: 'Delete',
                  tone: 'danger',
                })) {
                  deleteMutation.mutate(node.id);
                }
              }}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        {isExpanded && node.children?.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Categories"
        description={`${flatCount} categories across the catalog tree`}
        actions={
          <Button size="sm" onClick={() => setCreating({ parentId: null })}>
            <Plus className="h-4 w-4" /> New top-level category
          </Button>
        }
      />

      {isLoading ? (
        <TableSkeleton rows={10} cols={4} />
      ) : !tree?.length ? (
        <Card><CardContent>
          <EmptyState icon={FolderTree} title="No categories yet" description="Start with a top-level category like Electronics." />
        </CardContent></Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
            <div className="w-4" />
            <div className="w-7" />
            <div className="flex-1">Name</div>
            <div className="text-right pr-2">Status</div>
            <div className="w-8 text-right">Sort</div>
          </div>
          {tree.map((node) => renderNode(node))}
        </Card>
      )}

      {(creating || editing) && (
        <CategoryFormModal
          category={editing}
          parentId={creating?.parentId ?? editing?.parentId ?? null}
          tree={tree ?? []}
          onClose={() => {
            setCreating(null);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function flattenTree(nodes: Category[], depth = 0): Array<Category & { _depth: number }> {
  return nodes.flatMap((n) => [
    { ...n, _depth: depth },
    ...(n.children ? flattenTree(n.children, depth + 1) : []),
  ]);
}

function CategoryFormModal({
  category,
  parentId: initialParentId,
  tree,
  onClose,
}: {
  category: Category | null;
  parentId: string | null;
  tree: Category[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!category;
  const [name, setName] = useState(category?.name ?? '');
  const [nameNp, setNameNp] = useState(category?.nameNp ?? '');
  const [slug, setSlug] = useState(category?.slug ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  const [parentId, setParentId] = useState<string | null>(category?.parentId ?? initialParentId);
  const [sortOrder, setSortOrder] = useState(category?.sortOrder ?? 0);
  const [isFeatured, setIsFeatured] = useState(category?.isFeatured ?? false);
  const [isActive, setIsActive] = useState(category?.isActive ?? true);
  const [imageUrl, setImageUrl] = useState(category?.imageUrl ?? '');
  const [uploading, setUploading] = useState(false);

  const flat = useMemo(() => flattenTree(tree), [tree]);

  const onSlugify = (v: string) => {
    const s = v.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setSlug(s);
  };

  const uploadImage = async (file: File) => {
    setUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      const url = (res.data as any).data.url;
      setImageUrl(url);
      toast.success('Image uploaded');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        nameNp: nameNp || undefined,
        slug,
        description: description || undefined,
        parentId: parentId || undefined,
        sortOrder,
        isFeatured,
        isActive,
        imageUrl: imageUrl || undefined,
      };
      return isEdit
        ? adminApi.updateCategory(category!.id, payload)
        : adminApi.createCategory(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Category updated' : 'Category created');
      qc.invalidateQueries({ queryKey: ['admin', 'categories'] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">
            {isEdit ? `Edit "${category!.name}"` : 'New category'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Name (English)</label>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!isEdit) onSlugify(e.target.value);
              }}
              placeholder="e.g. Electronics"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Name (Nepali, optional)</label>
            <Input value={nameNp} onChange={(e) => setNameNp(e.target.value)} placeholder="इलेक्ट्रोनिक्स" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Slug</label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="electronics" className="font-mono" />
            <p className="text-xs text-slate-400 mt-1">URL: /category/{slug || '...'}</p>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Parent category</label>
            <select
              value={parentId ?? ''}
              onChange={(e) => setParentId(e.target.value || null)}
              className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
            >
              <option value="">— Top level —</option>
              {flat
                .filter((c) => c.id !== category?.id)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {'  '.repeat(c._depth)}
                    {c.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
              placeholder="Short description shown on the category page"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Image</label>
            {imageUrl ? (
              <div className="flex items-center gap-3">
                <img src={imageUrl} alt="" className="h-16 w-16 object-cover rounded-lg border border-slate-200" />
                <Button size="sm" variant="ghost" onClick={() => setImageUrl('')}>Remove</Button>
              </div>
            ) : (
              <label className="inline-block">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadImage(f);
                  }}
                />
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-dashed border-slate-300 text-xs text-slate-600 cursor-pointer hover:bg-slate-50">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                  {uploading ? 'Uploading…' : 'Upload image'}
                </span>
              </label>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Sort order</label>
              <Input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              />
            </div>
            <label className="flex items-center gap-2 mt-6 cursor-pointer">
              <input type="checkbox" checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} />
              <span className="text-sm text-slate-700">Featured</span>
            </label>
            <label className="flex items-center gap-2 mt-6 cursor-pointer">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span className="text-sm text-slate-700">Active</span>
            </label>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!name || !slug || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
            {isEdit ? 'Save changes' : 'Create category'}
          </Button>
        </div>
      </div>
    </div>
  );
}
